/**
 * Client for the Connected Runner registry on the public Ato API
 * (`/v1/runners`, ato-api PR #17).
 *
 * Security & boundary notes (same contract as RunsApiClient):
 *   - Auth is the account *cookie session* (`credentials: "include"`). It
 *     never sends a Runtime bearer token and never touches the
 *     DesktopRuntime control plane (`/v1/runtime/*`).
 *   - The PWA only ever sees registered devices and revokes them. Runner
 *     tokens are returned once at registration (done by the runner CLI,
 *     not the PWA) and are never readable here.
 *   - `online` is computed server-side from last_seen_at; the PWA renders
 *     it as-is and never invents liveness.
 */

import { accountApiBase, fetchWithConnectionAuth } from "../app/connection";

function defaultBaseUrl(): string {
  return accountApiBase();
}

export type RunnerKind =
  "desktop_companion" | "connected_runner" | "hosted_runner";

export interface RunnerDevice {
  id: string;
  display_name: string;
  kind: RunnerKind;
  /** Server-owned boundary; kind describes implementation only. */
  management_kind?: "ato_managed" | "user_managed";
  management_pool_id?: string | null;
  os: string | null;
  arch: string | null;
  capabilities: string[];
  supported_lease_kinds?: string[];
  public_base_url: string | null;
  status: "active" | "revoked";
  online: boolean;
  last_seen_at: string | null;
  created_at: string;
  /** The `ato` version the runner last reported (null until it reports one). */
  agent_version?: string | null;
  /** A self-update to this exact target is pending (set by Update; clears once
   *  the runner reports it reached the target). */
  update_requested_version?: string | null;
  /** The runner is older than the operator's configured target → "Update" is
   *  offered. False when no target is set or the runner is up to date. */
  update_available?: boolean;
  /** UI-only archive (hide) state; null unless archived. Distinct from
   *  `status: "revoked"`. The default list omits archived devices. */
  archived_at?: string | null;
}

export class RunnersAuthError extends Error {
  readonly status = 401;
  constructor() {
    super("Runners API: not signed in (401). Sign in to your Ato account.");
    this.name = "RunnersAuthError";
  }
}

export class RunnersConnectionError extends Error {
  constructor(cause?: unknown) {
    super(
      `Runners API: could not connect. ${cause instanceof Error ? cause.message : String(cause)}`,
    );
    this.name = "RunnersConnectionError";
  }
}

export class RunnersApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "RunnersApiError";
  }
}

export class RunnersApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(config: { baseUrl?: string; fetchImpl?: typeof fetch } = {}) {
    this.baseUrl = (config.baseUrl ?? defaultBaseUrl()).replace(/\/$/, "");
    this.fetchImpl = config.fetchImpl ?? fetch.bind(globalThis);
  }

  private async request<T>(
    path: string,
    method: "GET" | "POST",
    body?: unknown,
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetchWithConnectionAuth(
        `${this.baseUrl}${path}`,
        {
          method,
          headers:
            body === undefined ? {} : { "Content-Type": "application/json" },
          body: body === undefined ? undefined : JSON.stringify(body),
        }, this.fetchImpl);
    } catch (err) {
      throw new RunnersConnectionError(err);
    }

    if (res.status === 401) throw new RunnersAuthError();

    let json: unknown;
    try {
      json = await res.json();
    } catch (err) {
      throw new RunnersConnectionError(err);
    }

    if (!res.ok) {
      const j = json as Record<string, string> | undefined;
      const code = j?.["error"] ?? "runners_api_error";
      const message = j?.["message"] ?? `HTTP ${res.status}`;
      throw new RunnersApiError(res.status, code, message);
    }

    return json as T;
  }

  /** GET /v1/runners — list this account's runner devices. */
  async listRunners(): Promise<RunnerDevice[]> {
    const raw = await this.request<{ runners?: RunnerDevice[] }>(
      "/v1/runners",
      "GET",
    );
    return Array.isArray(raw.runners)
      ? raw.runners.filter((runner) => runner.management_kind !== "ato_managed")
      : [];
  }

  /** POST /v1/runners/:id/revoke — revoke a device (idempotent). */
  async revokeRunner(id: string): Promise<RunnerDevice | null> {
    const raw = await this.request<{ runner?: RunnerDevice }>(
      `/v1/runners/${encodeURIComponent(id)}/revoke`,
      "POST",
      {},
    );
    return raw.runner ?? null;
  }

  /**
   * POST /v1/runners/:id/archive — hide a (revoked) device. UI state only; the
   * API rejects archiving a non-revoked runner. Idempotent.
   */
  async archiveRunner(id: string): Promise<RunnerDevice | null> {
    const raw = await this.request<{ runner?: RunnerDevice }>(
      `/v1/runners/${encodeURIComponent(id)}/archive`,
      "POST",
      {},
    );
    return raw.runner ?? null;
  }

  /**
   * POST /v1/runners/:id/update — request a self-update for this runner. The
   * server uses its configured MINIMUM (RUNNER_MIN_AGENT_VERSION); the runner
   * picks the directive up on its next heartbeat and self-updates to the latest
   * release (which must satisfy the minimum) when idle. Returns the minimum the
   * request was recorded against.
   */
  async requestRunnerUpdate(id: string): Promise<string> {
    const raw = await this.request<{ minimum_version?: string }>(
      `/v1/runners/${encodeURIComponent(id)}/update`,
      "POST",
      {},
    );
    return raw.minimum_version ?? "";
  }
}
